/**
 * Main schematic canvas for the Hybrid System Integration Tool.
 * @module HybridSystemIntegration/components/SystemCanvas
 */

import React from 'react';
import { Card, Tag, Typography } from 'antd';
import { useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { COMPONENT_LIBRARY } from '../constants';
import { roundMetric } from '../utils';
import { FlowAnimationLayer } from './FlowAnimationLayer';
import styles from '../HybridSystemIntegration.module.css';

const { Text, Title } = Typography;

function CanvasNode({ node, selected, onSelect }) {
  const { t } = useTranslation();
  const component = COMPONENT_LIBRARY[node.type];
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: node.id,
    data: { nodeId: node.id },
  });

  const dragStyle = {
    left: node.x,
    top: node.y,
    borderColor: component.color,
    zIndex: isDragging ? 12 : 4,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
  };

  return (
    <button
      ref={setNodeRef}
      type="button"
      className={[
        styles.canvasNode,
        selected ? styles.canvasNodeSelected : '',
        isDragging ? styles.canvasNodeDragging : '',
      ].filter(Boolean).join(' ')}
      style={dragStyle}
      onPointerDown={() => onSelect(node.id)}
      {...listeners}
      {...attributes}
      aria-label={t(component.translationKey)}
    >
      <span className={styles.nodeIcon} style={{ backgroundColor: component.color }}>
        {component.icon}
      </span>
      <span className={styles.nodeLabel}>{t(component.translationKey)}</span>
    </button>
  );
}

/**
 * Animated hybrid heating system canvas.
 * @param {Object} props - Component props.
 * @returns {JSX.Element} System canvas card.
 */
export function SystemCanvas({
  nodes,
  selectedNodeId,
  onSelectNode,
  result,
  settings,
}) {
  const { t } = useTranslation();
  const coldIntensity = Math.max(0, Math.min(1, (3 - settings.outdoorTemperature) / 16));
  const sunOpacity = Math.max(0.14, settings.solarIrradiance * 0.7);

  return (
    <Card className={styles.canvasCard}>
      <div className={styles.canvasHeader}>
        <div>
          <Title level={4}>{t('hybridSystemTool.canvas.title')}</Title>
          <Text type="secondary">{t('hybridSystemTool.canvas.subtitle')}</Text>
        </div>
        <div className={styles.canvasTags}>
          <Tag>{roundMetric(settings.outdoorTemperature, 1)} C</Tag>
          <Tag>{settings.hourOfDay}:00</Tag>
          <Tag>{roundMetric(result.heatDemandKw, 1)} kW</Tag>
        </div>
      </div>

      <div className={styles.canvasFrame}>
        <div
          className={styles.systemCanvas}
          style={{ '--cold-intensity': coldIntensity }}
        >
          <div className={styles.weatherLayer}>
            <div className={styles.sunOrb} style={{ opacity: sunOpacity }} />
            {settings.outdoorTemperature < 0 && <div className={styles.snowLayer} aria-hidden="true" />}
          </div>

          <div className={styles.gridLayer} />
          <FlowAnimationLayer nodes={nodes} result={result} />

          {nodes.map((node) => (
            <CanvasNode
              key={node.id}
              node={node}
              selected={node.id === selectedNodeId}
              onSelect={onSelectNode}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

export default SystemCanvas;
